//
// DISCLAIMER
//
// Copyright 2019 ArangoDB GmbH, Cologne, Germany
//
// Author Ewout Prangsma
//

// Do not import from this file directly.
// Import from `lib.tsx` instead.

import React from "react";
import { Button, Dropdown, SemanticSIZES } from "semantic-ui-react";

// Page sizes offered by PagingButtons when onPageSizeChange is set
export const defaultPageSizeOptions = [10, 25, 50, 100];

interface IPagingButtons {
  // Page number; 0..
  page: number;
  // Number of items per page
  pageSize: number;
  // Size of button
  size?: SemanticSIZES;
  // Number of items on current page
  count: number;
  // Next page handler
  onNextPage?: () => void;
  // Previous page handler
  onPreviousPage?: () => void;
  // If set, a page size selector is shown
  onPageSizeChange?: (pageSize: number) => void;
  // Page sizes to choose from (defaults to defaultPageSizeOptions)
  pageSizeOptions?: number[];
}

export const PagingButtons = ({ ...args }: IPagingButtons) => {
  const hasNext = args.onNextPage && args.count == args.pageSize;
  const hasPrev = args.onPreviousPage && args.page > 0;
  const onPageSizeChange = args.onPageSizeChange;
  const pageSizeSelector = onPageSizeChange && (
    <Dropdown
      compact
      selection
      value={args.pageSize}
      text={`${args.pageSize} / page`}
      options={(args.pageSizeOptions || defaultPageSizeOptions).map((size) => ({ key: size, text: `${size} / page`, value: size }))}
      onChange={(e, d) => onPageSizeChange(d.value as number)}
    />
  );
  if (!hasNext && !hasPrev) {
    return pageSizeSelector ? <span style={{ float: "right" }}>{pageSizeSelector}</span> : <span></span>;
  }
  const buttons = (
    <Button.Group size={args.size || "mini"} floated={pageSizeSelector ? undefined : "right"}>
      <Button icon="angle left" disabled={!hasPrev} onClick={args.onPreviousPage} size={args.size} />
      <Button.Or text={`${args.page + 1}`} />
      <Button icon="angle right" disabled={!hasNext} onClick={args.onNextPage} size={args.size} />
    </Button.Group>
  );
  if (!pageSizeSelector) {
    return buttons;
  }
  return (
    <span style={{ float: "right" }}>
      {pageSizeSelector} {buttons}
    </span>
  );
};
